import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

export default function MiniAngelScene({ type = 'wing', size = 200 }) {
  const containerRef = useRef(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current || initRef.current) return;
    initRef.current = true;

    const container = containerRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(size, size);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
    scene.add(ambientLight);
    const dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.position.set(3, 3, 5);
    scene.add(dirLight);

    let mesh;

    if (type === 'wing') {
      const shape = new THREE.Shape();
      shape.moveTo(0, 0);
      shape.bezierCurveTo(0.3, 1.2, 0.9, 2.1, 0.2, 3);
      shape.bezierCurveTo(1.1, 2.4, 1.8, 2.1, 2.4, 2.7);
      shape.bezierCurveTo(2.1, 1.8, 1.5, 0.9, 0, 0);
      const geo = new THREE.ExtrudeGeometry(shape, { depth: 0.03, bevelEnabled: true, bevelThickness: 0.01, bevelSize: 0.01, bevelSegments: 2 });
      mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 0.9, roughness: 0.1 }));
      mesh.position.set(-1, -1.5, 0);
    } else if (type === 'halo') {
      const geo = new THREE.TorusGeometry(1.5, 0.02, 16, 100);
      mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 0.8, roughness: 0.2 }));
      mesh.rotation.x = Math.PI * 0.35;
    } else if (type === 'orb') {
      const geo = new THREE.IcosahedronGeometry(1.2, 1);
      mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0xffffff, wireframe: true, metalness: 0.6, roughness: 0.3 }));
    } else if (type === 'feather') {
      const shape = new THREE.Shape();
      shape.moveTo(0, 0);
      shape.quadraticCurveTo(0.3, 1.5, 0, 3);
      shape.quadraticCurveTo(-0.3, 1.5, 0, 0);
      const geo = new THREE.ExtrudeGeometry(shape, { depth: 0.01, bevelEnabled: false });
      mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 0.95, roughness: 0.05, side: THREE.DoubleSide }));
      mesh.position.set(0, -1.5, 0);
    }

    if (mesh) scene.add(mesh);

    const clock = new THREE.Clock();
    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      if (mesh) {
        mesh.rotation.y = t * 0.3;
        if (type === 'halo') mesh.rotation.z = t * 0.15;
        if (type === 'feather') {
          mesh.rotation.z = Math.sin(t * 0.5) * 0.3;
          mesh.position.y = -1.5 + Math.sin(t * 0.8) * 0.2;
        }
      }
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      initRef.current = false;
    };
  }, [type, size]);

  return (
    <div
      ref={containerRef}
      style={{ width: size, height: size, opacity: 0.15, pointerEvents: 'none' }}
      className="absolute"
    />
  );
}
